/**
 * Expected-rent engine: turns a tenancy's rent history into due instalments and
 * allocates received rent against them (oldest first).
 *
 * Conventions (documented in docs/adr/0006-rent-schedule.md):
 * - Rent is due in advance on the first day of each rental period.
 * - Month-based frequencies are anchored on `dueDay` (clamped to month end);
 *   week-based frequencies are anchored on the rent period's start date.
 * - A period cut short by a rent change or tenancy end is pro-rated by days of
 *   the full period that would have applied. Before the first anchor date a part
 *   period is charged on the same basis.
 */
import type { RentFrequency } from "../constants.ts";
import { addDays, addMonths, daysInclusive, type ISODate, maxDate, minDate, parts } from "../dates.ts";
import type { DateRange } from "../periods.ts";

export interface RentPeriodInput {
  effectiveFrom: ISODate;
  amountPence: number;
  frequency: RentFrequency;
  /** Day of month rent is due for month-based frequencies (1–31). */
  dueDay: number | null;
}

export interface TenancyInput {
  startDate: ISODate;
  /** Actual end date, or fixed-term end if no later rent is expected; null for open-ended. */
  endDate: ISODate | null;
}

export interface Instalment {
  dueDate: ISODate;
  periodFrom: ISODate;
  periodTo: ISODate;
  amountPence: number;
  /** Full-period rent, useful to show pro-rating. */
  fullAmountPence: number;
  prorated: boolean;
}

const MONTHS: Partial<Record<RentFrequency, number>> = { monthly: 1, quarterly: 3, annually: 12 };
const DAYS: Partial<Record<RentFrequency, number>> = { weekly: 7, fortnightly: 14, four_weekly: 28 };

function nextStart(start: ISODate, freq: RentFrequency, anchorDay: number): ISODate {
  const months = MONTHS[freq];
  return months ? addMonths(start, months, anchorDay) : addDays(start, DAYS[freq]!);
}

function prevStart(start: ISODate, freq: RentFrequency, anchorDay: number): ISODate {
  const months = MONTHS[freq];
  return months ? addMonths(start, -months, anchorDay) : addDays(start, -DAYS[freq]!);
}

/** First date ≥ from whose day-of-month equals dueDay (clamped to month end). */
function firstAnchor(from: ISODate, dueDay: number): ISODate {
  const sameMonth = addMonths(from, 0, dueDay);
  return sameMonth >= from ? sameMonth : addMonths(from, 1, dueDay);
}

function prorate(amount: number, coveredDays: number, fullDays: number): number {
  return Math.round((amount * coveredDays) / fullDays);
}

/**
 * All instalments for a tenancy, optionally limited to those due up to `until`
 * (required for open-ended tenancies).
 */
export function rentSchedule(
  tenancy: TenancyInput,
  periods: RentPeriodInput[],
  until: ISODate,
): Instalment[] {
  const sorted = [...periods].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1));
  // `until` only limits which due dates are generated; it never truncates a period.
  const tenancyEnd = tenancy.endDate ?? "9999-12-31";
  const out: Instalment[] = [];

  sorted.forEach((period, idx) => {
    const segFrom = maxDate(period.effectiveFrom, tenancy.startDate);
    const next = sorted[idx + 1];
    const segTo = minDate(next ? addDays(next.effectiveFrom, -1) : tenancyEnd, tenancyEnd);
    if (segFrom > segTo || segFrom > until) return;

    const monthBased = MONTHS[period.frequency] !== undefined;
    const anchorDay = monthBased ? (period.dueDay ?? parts(segFrom).d) : 0;
    const anchor = monthBased ? firstAnchor(segFrom, anchorDay) : segFrom;

    const push = (from: ISODate, fullFrom: ISODate, fullTo: ISODate) => {
      const to = minDate(fullTo, segTo);
      const fullDays = daysInclusive(fullFrom, fullTo);
      const covered = daysInclusive(from, to);
      const prorated = covered !== fullDays;
      out.push({
        dueDate: from,
        periodFrom: from,
        periodTo: to,
        amountPence: prorated ? prorate(period.amountPence, covered, fullDays) : period.amountPence,
        fullAmountPence: period.amountPence,
        prorated,
      });
    };

    // Part period before the first anchor date.
    if (anchor > segFrom && segFrom <= until) {
      const fullFrom = prevStart(anchor, period.frequency, anchorDay);
      push(segFrom, fullFrom, addDays(anchor, -1));
    }
    let start = anchor;
    while (start <= segTo && start <= until) {
      const end = addDays(nextStart(start, period.frequency, anchorDay), -1);
      push(start, start, end);
      start = addDays(end, 1);
    }
  });
  return out;
}

export interface Payment {
  id: string;
  date: ISODate;
  amountPence: number;
}

export type InstalmentStatus = "paid" | "partial" | "unpaid" | "overdue" | "upcoming";

export interface AllocatedInstalment extends Instalment {
  paidPence: number;
  outstandingPence: number;
  status: InstalmentStatus;
}

/** Allocates payments to instalments oldest-first. Surplus payments become credit. */
export function allocatePayments(
  instalments: Instalment[],
  payments: Payment[],
  asOf: ISODate,
): { instalments: AllocatedInstalment[]; creditPence: number } {
  let pool = payments.reduce((sum, p) => sum + p.amountPence, 0);
  const sorted = [...instalments].sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const allocated = sorted.map((inst) => {
    const paid = Math.max(0, Math.min(inst.amountPence, pool));
    pool -= paid;
    const outstanding = inst.amountPence - paid;
    let status: InstalmentStatus;
    if (outstanding <= 0) status = "paid";
    else if (inst.dueDate > asOf) status = "upcoming";
    else if (paid > 0) status = "partial";
    else status = inst.dueDate < asOf ? "overdue" : "unpaid";
    return { ...inst, paidPence: paid, outstandingPence: outstanding, status };
  });
  return { instalments: allocated, creditPence: Math.max(0, pool) };
}

export interface RentSummary {
  expectedPence: number;
  receivedPence: number;
  /** Arrears as at the end of the range (all instalments due by then, less all payments by then). */
  outstandingPence: number;
  creditPence: number;
}

/** Expected vs received for a range, and arrears position at the end of the range. */
export function rentSummary(
  tenancy: TenancyInput,
  periods: RentPeriodInput[],
  payments: Payment[],
  range: DateRange,
): RentSummary {
  const schedule = rentSchedule(tenancy, periods, range.to);
  const expectedPence = schedule
    .filter((i) => i.dueDate >= range.from && i.dueDate <= range.to)
    .reduce((s, i) => s + i.amountPence, 0);
  const receivedPence = payments
    .filter((p) => p.date >= range.from && p.date <= range.to)
    .reduce((s, p) => s + p.amountPence, 0);
  const dueToDate = schedule.reduce((s, i) => s + i.amountPence, 0);
  const paidToDate = payments.filter((p) => p.date <= range.to).reduce((s, p) => s + p.amountPence, 0);
  const balance = dueToDate - paidToDate;
  return {
    expectedPence,
    receivedPence,
    outstandingPence: Math.max(0, balance),
    creditPence: Math.max(0, -balance),
  };
}

/** The rent period in effect on a date, if any. */
export function rentOn(periods: RentPeriodInput[], date: ISODate): RentPeriodInput | null {
  let current: RentPeriodInput | null = null;
  for (const p of [...periods].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1))) {
    if (p.effectiveFrom <= date) current = p;
  }
  return current;
}

/** Annualised rent in pence for comparison across frequencies. */
export function annualise(amountPence: number, frequency: RentFrequency): number {
  switch (frequency) {
    case "weekly":
      return Math.round((amountPence * 365) / 7);
    case "fortnightly":
      return Math.round((amountPence * 365) / 14);
    case "four_weekly":
      return Math.round((amountPence * 365) / 28);
    case "monthly":
      return amountPence * 12;
    case "quarterly":
      return amountPence * 4;
    case "annually":
      return amountPence;
  }
}
